import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { authClient, useSession } from "../lib/auth.ts";

type InvitationDetail = {
  id: string;
  email: string;
  role: string;
  status: string;
  expiresAt: string | Date;
  organizationName: string;
  organizationSlug: string;
  inviterEmail?: string;
};

export function InvitePage() {
  const { id } = useParams<{ id: string }>();
  const { data: session, isPending, refetch } = useSession();
  const [invitation, setInvitation] = useState<InvitationDetail | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<"accept" | "reject" | null>(null);
  const [done, setDone] = useState<"accepted" | "rejected" | null>(null);

  useEffect(() => {
    if (!id) return;
    authClient.organization
      .getInvitation({ query: { id } })
      .then((r) => {
        if (r.error) {
          setLoadErr(r.error.message ?? "Couldn't load invitation.");
          return;
        }
        setInvitation(r.data as InvitationDetail);
      })
      .catch((e) => setLoadErr((e as Error).message));
  }, [id]);

  if (isPending) return <div className="center">Loading…</div>;

  // Sending the user to /login with a `next` param so they come back here
  // after authenticating. The login page does nav("/") on success today;
  // a future polish item is honouring `next` for deeper return paths.
  if (!session?.user) {
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(`/invite/${id ?? ""}`)}`}
        replace
      />
    );
  }

  if (done === "accepted") {
    return (
      <div className="center">
        <div className="card narrow text-center">
          <h2>Joined!</h2>
          <p className="muted">
            You're now a member of {invitation?.organizationName}. The active
            organisation has been set; it might take a second to take effect.
          </p>
          <Link to="/">Go to dashboard</Link>
        </div>
      </div>
    );
  }
  if (done === "rejected") {
    return (
      <div className="center">
        <div className="card narrow text-center">
          <h2>Invitation declined.</h2>
          <Link to="/">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="center">
      <div className="card narrow">
        <h2>Organisation invitation</h2>
        {loadErr && <p className="err">{loadErr}</p>}
        {!loadErr && !invitation && <p className="muted">Loading…</p>}
        {invitation && (
          <>
            <p>
              You've been invited to join{" "}
              <strong>{invitation.organizationName}</strong> as{" "}
              <strong>{invitation.role}</strong>.
            </p>
            <p className="muted">
              Sent to {invitation.email}. Expires{" "}
              {new Date(invitation.expiresAt).toLocaleString()}.
              {invitation.email !== session.user.email && (
                <>
                  {" "}
                  You're signed in as <code>{session.user.email}</code> — that
                  doesn't match the invited address; the server will reject
                  the accept call.
                </>
              )}
            </p>
            {actionErr && <p className="err">{actionErr}</p>}
            <div className="row mt">
              <button
                disabled={busy !== null}
                onClick={async () => {
                  setBusy("accept");
                  setActionErr(null);
                  const r = await authClient.organization.acceptInvitation({
                    invitationId: invitation.id,
                  });
                  if (r.error) {
                    setBusy(null);
                    setActionErr(r.error.message ?? "Couldn't accept invitation.");
                    return;
                  }
                  // acceptInvitation already calls setActive server-side, but
                  // the client session cache won't reflect it until we
                  // refetch. Force-fresh so the next page sees the new
                  // active org.
                  await refetch({ query: { disableCookieCache: true } });
                  setBusy(null);
                  setDone("accepted");
                }}
              >
                {busy === "accept" ? "Joining…" : "Accept"}
              </button>
              <button
                className="secondary"
                disabled={busy !== null}
                onClick={async () => {
                  setBusy("reject");
                  setActionErr(null);
                  const r = await authClient.organization.rejectInvitation({
                    invitationId: invitation.id,
                  });
                  if (r.error) {
                    setBusy(null);
                    setActionErr(r.error.message ?? "Couldn't decline.");
                    return;
                  }
                  setBusy(null);
                  setDone("rejected");
                }}
              >
                {busy === "reject" ? "…" : "Decline"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
